/**
 * Validation Tab Component
 * Configure validation rules for form controls
 */

import React, { useState, useCallback } from 'react';
import { useUIStore } from '@atlas-forms/state-react';
import { CheckIcon } from '@atlas-forms/ui-components-react';
import type { FormControl, FieldValidation } from '@atlas-forms/types-js';
import '../../styles/tabs/ValidationTab.css';

export interface ValidationTabProps {
  controls: FormControl[];
  onUpdateControl: (id: string, updates: Partial<FormControl>) => void;
}

/**
 * Validation Tab - Field and form-level validation rules
 */
export const ValidationTab: React.FC<ValidationTabProps> = ({ controls, onUpdateControl }) => {
  const { theme } = useUIStore();
  const [selectedControlId, setSelectedControlId] = useState<string | null>(
    controls.length > 0 ? controls[0].id : null
  );

  const selectedControl = controls.find((c) => c.id === selectedControlId);

  const handleValidationChange = useCallback(
    (controlId: string, key: keyof FieldValidation, value: unknown) => {
      const control = controls.find((c) => c.id === controlId);
      if (control) {
        onUpdateControl(controlId, {
          validation: {
            ...control.validation,
            [key]: value,
          },
        });
      }
    },
    [controls, onUpdateControl]
  );

  const handleClearValidation = useCallback(
    (controlId: string) => {
      onUpdateControl(controlId, {
        validation: undefined,
      });
    },
    [onUpdateControl]
  );

  return (
    <div className="af-validation-tab" data-theme={theme}>
      <div className="af-validation-tab__layout">
        {/* Controls List */}
        <div className="af-validation-tab__controls-list">
          <h3 className="af-validation-tab__list-title">Form Controls</h3>

          {controls.length === 0 ? (
            <p className="af-validation-tab__empty-text">No controls in form</p>
          ) : (
            <div className="af-validation-tab__list">
              {controls.map((control) => (
                <button
                  key={control.id}
                  onClick={() => setSelectedControlId(control.id)}
                  className={`af-validation-tab__list-item ${
                    selectedControlId === control.id ? 'af-validation-tab__list-item--selected' : ''
                  }`}
                  title={control.description}
                >
                  <span className="af-validation-tab__list-item-label">
                    {control.label || control.id}
                  </span>
                  <span className="af-validation-tab__list-item-type">
                    {control.type}
                  </span>
                  {control.required && (
                    <span className="af-validation-tab__required-badge">*</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Validation Rules */}
        <div className="af-validation-tab__rules">
          {selectedControl ? (
            <>
              <div className="af-validation-tab__header">
                <h3 className="af-validation-tab__title">
                  Validation Rules: {selectedControl.label || selectedControl.id}
                </h3>
                <button
                  onClick={() => handleClearValidation(selectedControl.id)}
                  className="af-validation-tab__clear-btn"
                >
                  Clear All
                </button>
              </div>

              <div className="af-validation-tab__section">
                <h4 className="af-validation-tab__subsection">Basic Rules</h4>

                <div className="af-validation-tab__rule">
                  <label className="af-validation-tab__checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedControl.required || false}
                      onChange={(e) =>
                        onUpdateControl(selectedControl.id, {
                          required: e.target.checked,
                        })
                      }
                    />
                    Required Field
                  </label>
                  <p className="af-validation-tab__rule-description">
                    This field must be filled in
                  </p>
                </div>
              </div>

              <div className="af-validation-tab__section">
                <h4 className="af-validation-tab__subsection">String Validation</h4>

                <div className="af-validation-tab__rule">
                  <label className="af-validation-tab__label">
                    Minimum Length
                    <input
                      type="number"
                      min="0"
                      value={selectedControl.validation?.minLength || ''}
                      onChange={(e) =>
                        handleValidationChange(
                          selectedControl.id,
                          'minLength',
                          e.target.value ? parseInt(e.target.value) : undefined
                        )
                      }
                      className="af-validation-tab__input"
                      placeholder="Leave empty for no limit"
                    />
                  </label>
                </div>

                <div className="af-validation-tab__rule">
                  <label className="af-validation-tab__label">
                    Maximum Length
                    <input
                      type="number"
                      min="0"
                      value={selectedControl.validation?.maxLength || ''}
                      onChange={(e) =>
                        handleValidationChange(
                          selectedControl.id,
                          'maxLength',
                          e.target.value ? parseInt(e.target.value) : undefined
                        )
                      }
                      className="af-validation-tab__input"
                      placeholder="Leave empty for no limit"
                    />
                  </label>
                </div>
              </div>

              <div className="af-validation-tab__section">
                <h4 className="af-validation-tab__subsection">Numeric Validation</h4>

                <div className="af-validation-tab__rule">
                  <label className="af-validation-tab__label">
                    Minimum Value
                    <input
                      type="number"
                      value={selectedControl.validation?.min !== undefined ? selectedControl.validation.min : ''}
                      onChange={(e) =>
                        handleValidationChange(
                          selectedControl.id,
                          'min',
                          e.target.value ? parseFloat(e.target.value) : undefined
                        )
                      }
                      className="af-validation-tab__input"
                      placeholder="Leave empty for no limit"
                    />
                  </label>
                </div>

                <div className="af-validation-tab__rule">
                  <label className="af-validation-tab__label">
                    Maximum Value
                    <input
                      type="number"
                      value={selectedControl.validation?.max !== undefined ? selectedControl.validation.max : ''}
                      onChange={(e) =>
                        handleValidationChange(
                          selectedControl.id,
                          'max',
                          e.target.value ? parseFloat(e.target.value) : undefined
                        )
                      }
                      className="af-validation-tab__input"
                      placeholder="Leave empty for no limit"
                    />
                  </label>
                </div>
              </div>

              <div className="af-validation-tab__section">
                <h4 className="af-validation-tab__subsection">Pattern Validation</h4>

                <div className="af-validation-tab__rule">
                  <label className="af-validation-tab__label">
                    Regular Expression
                    <input
                      type="text"
                      value={selectedControl.validation?.pattern || ''}
                      onChange={(e) =>
                        handleValidationChange(
                          selectedControl.id,
                          'pattern',
                          e.target.value
                        )
                      }
                      className="af-validation-tab__input"
                      placeholder="e.g., ^[a-z]+$"
                    />
                  </label>
                  <p className="af-validation-tab__rule-description">
                    JavaScript regex pattern that the value must match
                  </p>
                </div>
              </div>

              <div className="af-validation-tab__section">
                <h4 className="af-validation-tab__subsection">Error Message</h4>

                <div className="af-validation-tab__rule">
                  <label className="af-validation-tab__label">
                    Custom Error Message
                    <textarea
                      value={selectedControl.validation?.message || ''}
                      onChange={(e) =>
                        handleValidationChange(
                          selectedControl.id,
                          'message',
                          e.target.value
                        )
                      }
                      className="af-validation-tab__textarea"
                      placeholder="Message shown when validation fails"
                      rows={3}
                    />
                  </label>
                </div>
              </div>

              {selectedControl.validation && Object.keys(selectedControl.validation).length > 0 && (
                <div className="af-validation-tab__section">
                  <h4 className="af-validation-tab__subsection">Current Rules Summary</h4>
                  <div className="af-validation-tab__summary">
                    {selectedControl.required && <div><CheckIcon width={12} height={12} /> Field is required</div>}
                    {selectedControl.validation?.minLength && (
                      <div><CheckIcon width={12} height={12} /> Minimum {selectedControl.validation.minLength} characters</div>
                    )}
                    {selectedControl.validation?.maxLength && (
                      <div><CheckIcon width={12} height={12} /> Maximum {selectedControl.validation.maxLength} characters</div>
                    )}
                    {selectedControl.validation?.min !== undefined && (
                      <div><CheckIcon width={12} height={12} /> Minimum value: {selectedControl.validation.min}</div>
                    )}
                    {selectedControl.validation?.max !== undefined && (
                      <div><CheckIcon width={12} height={12} /> Maximum value: {selectedControl.validation.max}</div>
                    )}
                    {selectedControl.validation?.pattern && (
                      <div><CheckIcon width={12} height={12} /> Must match pattern: {selectedControl.validation.pattern}</div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="af-validation-tab__empty-state">
              <p>No control selected</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

ValidationTab.displayName = 'ValidationTab';
